import { formatPesos, formatShortDate, PillTone } from '@shared/components/ciec-analysis-view/snapshot-labels.constants'
import { FxConversion, MetricState, MetricUnit, RentSource, RiskBand } from '@data/interfaces/risk-scorecard.interface'

/**
 * Rótulos y formatos de la calculadora de riesgo y sus pestañas (plan v4.2 §8 y §8b). El motor manda claves; aquí se
 * dicen en español. Una clave que este archivo no conoce se pinta legible, nunca se esconde.
 */

export const BAND_UI: Readonly<Record<RiskBand, { label: string; tone: PillTone }>> = {
  favorable: { label: 'Favorable', tone: 'ok' },
  review: { label: 'Revisar', tone: 'warn' },
  unfavorable: { label: 'Desfavorable', tone: 'bad' },
}

export const METRIC_STATE_UI: Readonly<Record<MetricState, { label: string; tone: PillTone; hint: string }>> = {
  real: { label: 'Real', tone: 'ok', hint: 'Calculada con datos completos.' },
  parcial: { label: 'Parcial', tone: 'warn', hint: 'Insumo incompleto: no pasa de 10 puntos.' },
  sin_dato: { label: 'Sin dato', tone: 'neutral', hint: 'Sin dato: vale el tramo neutro y no suma cobertura.' },
  no_aplica: { label: 'No aplica', tone: 'neutral', hint: 'Métrica opcional sin dato: vale el tramo neutro y no cuenta en la cobertura.' },
}

export const RENT_SOURCE_LABELS: Readonly<Record<RentSource, string>> = {
  conservative_offer: 'pago de la oferta conservadora',
  quote: 'renta de la cotización del arrendamiento',
  conservative_offer_no_quote: 'pago de la oferta conservadora (el arrendamiento aún no tiene cotización)',
  none: 'sin renta: no hay oferta ni cotización',
}

export const CREDIT_SOURCE_LABELS: Readonly<Partial<Record<string, string>>> = {
  syntage_buro: 'Buró de Crédito (consultado por Syntage)',
  circulo: 'Círculo de Crédito',
}

//* Marcas sin parámetro. Las que llevan `:valor` se resuelven en `flagLabel`.
const FLAG_LABELS: Readonly<Record<string, string>> = {
  sin_historial_crediticio: 'Sin historial crediticio',
  historial_no_consultado: 'Hay formato de autorización aprobado pero ninguna consulta a Círculo',
  historial_desactualizado: 'Historial crediticio de más de 90 días',
  sic_incompleto: 'Reporte del SIC incompleto',
  cuentas_convertidas_a_pesos: 'Incluye cuentas en otra moneda convertidas a pesos',
  cuentas_sin_tipo_de_cambio: 'Cuentas en otra moneda sin tipo de cambio: quedaron fuera',
  sin_renta: 'Sin renta de referencia',
  renta_de_oferta_sin_cotizacion: 'Renta de la oferta conservadora: el arrendamiento aún no tiene cotización',
  aviso_pld: 'Aviso PLD',
  solo_en_arrendamiento: 'Se captura en el arrendamiento',
  sin_captura: 'Sin captura del analista',
  antecedentes_legales_no_consultados: 'Antecedentes legales no consultados',
  antecedente_penal_como_demandado: 'Antecedente penal como demandado',
  bil_con_grado: 'Coincidencia en BIL con grado',
  sin_base_previa: 'Sin base previa para comparar',
  revisar_crecimiento: 'Crecimiento mayor a 100 %: revisar',
  ventana_corta: 'Menos de 12 meses de flujo',
  sin_flujo_cfdi: 'Sin flujo CFDI',
  servicio_estimado: 'Servicio de deuda estimado',
  sin_servicio_de_deuda_medible: 'Sin servicio de deuda medible',
  ebitda_no_positivo: 'EBITDA en cero o negativo',
  depreciacion_no_confiable: 'Depreciación no confiable: EBITDA = utilidad de operación',
  capital_negativo: 'Capital contable negativo',
  denominador_no_positivo: 'Denominador en cero o negativo',
  ejercicio_incompleto: 'Ejercicio incompleto',
  umbrales_en_validacion: 'Umbrales en validación por Finanzas',
  foto_pendiente: 'Foto con secciones pendientes',
  sin_entidad_syntage: 'El cliente no está registrado en Syntage',
  foto_reconstruida: 'Foto reconstruida después del análisis',
  sin_obligado_solidario: 'Sin obligado solidario registrado en los contactos del cliente',
  segundo_obligado_sin_documentos: 'El 2º obligado solidario todavía no tiene documentos aprobados',
  obligado_sin_rfc: 'Hay obligados sin RFC válido (o repetidos): no cuentan',
  obligados_no_sellados: 'Sello anterior al motor 2: los obligados son los de hoy',
}

const PILLAR_NAMES: Readonly<Record<string, string>> = { character: 'Carácter', capacity: 'Capacidad', capital: 'Capital' }

//* De dónde salió el score de Buró (H5).
const FUENTE_LABELS: Readonly<Record<string, string>> = {
  buro_detalle: 'Del reporte de Buró de la foto',
  buro_resumen: 'Del resumen del Buró de la foto',
  syntage_scores: 'De los scores de Syntage (fuente Buró)',
}

/** Una marca del motor en español; `meses:2023=3` → «2023: 3 meses con flujo». */
export function flagLabel(flag: string): string {
  if (FLAG_LABELS[flag]) return FLAG_LABELS[flag]
  const [name, param = ''] = flag.split(/:(.*)/s)
  switch (name) {
    case 'meses': {
      const [year, months] = param.split('=')
      return `${year}: ${months} ${months === '1' ? 'mes' : 'meses'} con flujo`
    }
    case 'flujo_inconsistente':
      return `El flujo de 12 meses contradice al de ${param}: no pasa de 10 puntos`
    case 'eeff_desfasado':
      return `Ejercicio ${param} incompleto: se usó el anterior`
    case 'meses_con_dato':
      return `${param} meses con dato`
    case 'nodo_sin_dato':
      return `Sin dato en el balance: ${param.replace(/_/g, ' ')}`
    case 'vencido':
      return `Vencido: ${(Number(param) * 100).toFixed(1)} % del saldo`
    case 'instituciones':
      return `Recurrente con ${param} ${param === '1' ? 'institución' : 'instituciones'}`
    case 'calculado':
      return `Calculado por Syntage el ${formatShortDate(param)}`
    case 'low_coverage':
      return `Cobertura baja en ${PILLAR_NAMES[param] ?? param}`
    case 'fuente':
      return FUENTE_LABELS[param] ?? `Fuente: ${param}`
    default: {
      const base = FLAG_LABELS[name] ?? humanize(name)
      return param ? `${base} (${param})` : base
    }
  }
}

//* Marcas que se repiten con distinto parámetro y se leen mejor juntas: «(2024, 2025)» en vez de dos veces lo mismo.
const MERGEABLE: Readonly<Record<string, (params: string[]) => string>> = {
  nodo_sin_dato: (params) => `Sin dato en el balance: ${params.map((param) => param.replace(/_/g, ' ')).join(', ')}`,
  denominador_no_positivo: (params) => `Denominador en cero o negativo (${params.join(', ')})`,
  capital_negativo: (params) => `Capital contable negativo (${params.join(', ')})`,
}

/** Las marcas de una métrica o un ratio en una línea, con las repetidas juntas. */
export function flagsText(flags: string[]): string {
  const groups = new Map<string, string[]>()
  for (const flag of flags) {
    const [name, param = ''] = flag.split(/:(.*)/s)
    groups.set(name, [...(groups.get(name) ?? []), param])
  }
  return [...groups]
    .map(([name, params]) => (params.length > 1 && MERGEABLE[name] ? MERGEABLE[name](params) : params.map((param) => flagLabel(param ? `${name}:${param}` : name)).join(' · ')))
    .join(' · ')
}

function humanize(key: string): string {
  const text = key.replace(/_/g, ' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** El valor de una métrica o un ratio en su unidad. Los porcentajes llegan como fracción (0.08 = 8 %). */
export function formatMetricValue(value: number | null | undefined, unit: MetricUnit): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  switch (unit) {
    case '%':
      return `${value < 0 ? '−' : ''}${Math.abs(value * 100).toFixed(1)} %`
    case 'x':
      return `${value < 0 ? '−' : ''}${Math.abs(value).toFixed(2)}x`
    case 'days':
      return `${Math.round(value)} días`
    case 'mxn':
      return formatPesos(value)
    case 'years':
      return `${value.toFixed(1)} años`
    case 'hhi':
    case 'count':
      return Math.round(value).toLocaleString('es-MX')
    case 'pts':
      return value.toFixed(0)
    default:
      return value.toFixed(2)
  }
}

/** Un puntaje con dos decimales: la banda compara el número sin redondear (R-09), esto es solo presentación. */
export function formatScore(value: number | null | undefined): string {
  //* 40.365 llega del motor como 40.36499999…: sin el ajuste se pintaría 40.36. El margen es mucho menor que un centésimo.
  return value === null || value === undefined || !Number.isFinite(value) ? '—' : (Math.round(value * 100 + 1e-7) / 100).toFixed(2)
}

/** Cobertura en puntos básicos → «92.3 %». */
export function formatCoverage(bp: number | null | undefined): string {
  return bp === null || bp === undefined ? '—' : `${(bp / 100).toFixed(1)} %`
}

/** Mes `AAAA-MM` → «jul 2026». */
export function formatMonth(month: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month ?? '')
  if (!match) return month ?? '—'
  return new Date(Number(match[1]), Number(match[2]) - 1, 1).toLocaleDateString('es-MX', { month: 'short', year: 'numeric' }).replace(/\./g, '')
}

/**
 * La leyenda de una conversión de moneda (D-44): «2 cuentas en USD convertidas a pesos a 18.4500 (tipo de cambio que
 * reporta el Buró)». El tipo de cambio se muestra completo: es lo que hay que poder auditar.
 */
export function fxLegend(conversion: FxConversion): string {
  const accounts = `${conversion.accounts} ${conversion.accounts === 1 ? 'cuenta' : 'cuentas'} en ${conversion.currency}`
  const source = conversion.source === 'buro' ? 'tipo de cambio que reporta el Buró' : `FIX de Banxico${conversion.date ? ` del ${formatShortDate(conversion.date)}` : ''}`
  return `${accounts} convertidas a pesos a ${conversion.rate.toFixed(4)} (${source})`
}
